import { createSaleAddFromResponse } from "@/pages/point-of-sale/adapters/sale-add.adapter";
import { ISaleAddModel } from "@/pages/point-of-sale/models/sale-add.model";
import { SalesResponse } from "@/pages/[others]/pages/sales/models/sale.interface";
import { resilientPost } from "@/interceptors/resilient.interceptor";
import { useSaleStore } from "@/store/sale.store";
import { useSyncStore, SyncResult } from "@/store/sync.store";
import {
  getCajaCerradaMessage,
  isCajaCerradaError,
} from "@/utilities/caja-cerrada.utils";
import { isOfflineUuidSyncedInStore } from "@/utilities/sales-dedup.utils";
import { canRunOfflineSync, resolveCashierId } from "@/utilities/sync-session.utils";
import {
  extractSyncErrorMessage,
  isStoreSaleSyncExhausted,
  MAX_OFFLINE_SYNC_ATTEMPTS,
  notifyStoreSalesNewlyExhausted,
  OFFLINE_SALE_CAJA_CERRADA_MSG,
  OFFLINE_SALE_STALE_CASHIER_MSG,
  type StoreSyncBatchResult,
} from "@/utilities/offline-sync-retry.constants";

function buildDisplayData(sale: SalesResponse): SyncResult["displayData"] {
  const rawAmount = sale.bills?.[0]?.total_amount;
  return {
    amount: typeof rawAmount === "string" ? parseFloat(rawAmount) : rawAmount,
    customerName: sale.customer?.name,
    documentType: sale.bills?.[0]?.bill_type?.name,
  };
}

/**
 * Envía al servidor las ventas creadas offline que viven en sale.store (Zustand/IDB).
 * No usa PouchDB; es la fuente usada por useSaveSale cuando !isOnline.
 */
export async function syncUnsyncedStoreSales(): Promise<StoreSyncBatchResult> {
  const {
    getUnsyncedSales,
    getUnsyncedSalesForAutoSync,
    markSaleAsSynced,
    markStoreSaleSyncBlocked,
    recordStoreSaleSyncFailure,
  } = useSaleStore.getState();

  const allPending = getUnsyncedSales();
  const unsynced = getUnsyncedSalesForAutoSync();
  const skippedExhausted = allPending.length - unsynced.length;

  if (allPending.length === 0) {
    return {
      success: 0,
      errors: 0,
      aborted: false,
      skippedExhausted: 0,
      newlyExhausted: 0,
    };
  }

  if (unsynced.length === 0) {
    return {
      success: 0,
      errors: 0,
      aborted: false,
      skippedExhausted,
      newlyExhausted: 0,
    };
  }

  if (!canRunOfflineSync()) {
    console.warn(
      "[Sync] Ventas pendientes en espera: abre la caja antes de sincronizar."
    );
    return {
      success: 0,
      errors: 0,
      aborted: false,
      skippedExhausted,
      newlyExhausted: 0,
    };
  }

  const syncStore = useSyncStore.getState();
  let successCount = 0;
  let errorCount = 0;
  const newlyExhaustedUuids: string[] = [];

  for (const sale of unsynced) {
    const offlineUuid = sale.offline_uuid;
    if (!offlineUuid) continue;

    if (isOfflineUuidSyncedInStore(offlineUuid)) {
      const { removePouchPostsForOfflineUuid } = await import(
        "@/utilities/offline-db"
      );
      await removePouchPostsForOfflineUuid(offlineUuid);
      continue;
    }

    const recordId = offlineUuid;
    const displayData = buildDisplayData(sale);

    syncStore.markSyncing(recordId);

    const currentCashierId = resolveCashierId();
    const saleCashierId = sale.cashier?.id;
    if (
      currentCashierId &&
      saleCashierId &&
      String(saleCashierId) !== currentCashierId
    ) {
      const wasExhausted = isStoreSaleSyncExhausted(sale);
      markStoreSaleSyncBlocked(offlineUuid, OFFLINE_SALE_STALE_CASHIER_MSG);

      if (!wasExhausted) {
        newlyExhaustedUuids.push(offlineUuid);
      }

      errorCount++;
      syncStore.addResult({
        id: recordId,
        type: "sale",
        status: "error",
        errorMessage: OFFLINE_SALE_STALE_CASHIER_MSG,
        displayData,
      });
      continue;
    }

    try {
      const salePayload = createSaleAddFromResponse(sale);
      const response = await resilientPost<ISaleAddModel, { id: number }>(
        "sales",
        salePayload
      );

      if (response?.id) {
        markSaleAsSynced(offlineUuid, response.id);
        const { removePouchPostsForOfflineUuid } = await import(
          "@/utilities/offline-db"
        );
        await removePouchPostsForOfflineUuid(offlineUuid);
        successCount++;
        syncStore.addResult({
          id: recordId,
          type: "sale",
          status: "success",
          displayData,
        });
      }
    } catch (error: unknown) {
      if (isCajaCerradaError(error)) {
        const errorMessage =
          getCajaCerradaMessage(error) || OFFLINE_SALE_CAJA_CERRADA_MSG;
        const wasExhausted = isStoreSaleSyncExhausted(sale);

        markStoreSaleSyncBlocked(offlineUuid, errorMessage);

        if (!wasExhausted) {
          newlyExhaustedUuids.push(offlineUuid);
        }

        errorCount++;
        syncStore.addResult({
          id: recordId,
          type: "sale",
          status: "error",
          errorMessage,
          displayData,
        });
        continue;
      }

      const errorMessage = extractSyncErrorMessage(error);
      const wasExhausted = isStoreSaleSyncExhausted(sale);

      recordStoreSaleSyncFailure(offlineUuid, errorMessage);

      const updatedSale = useSaleStore
        .getState()
        .getUnsyncedSales()
        .find((s) => s.offline_uuid === offlineUuid);

      const nowExhausted =
        !wasExhausted && updatedSale && isStoreSaleSyncExhausted(updatedSale);

      if (nowExhausted) {
        newlyExhaustedUuids.push(offlineUuid);
      }

      errorCount++;
      syncStore.addResult({
        id: recordId,
        type: "sale",
        status: nowExhausted ? "error" : "error",
        errorMessage: nowExhausted
          ? `${errorMessage} (máx. ${MAX_OFFLINE_SYNC_ATTEMPTS} intentos)`
          : errorMessage,
        displayData,
      });
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const result: StoreSyncBatchResult = {
    success: successCount,
    errors: errorCount,
    aborted: false,
    skippedExhausted,
    newlyExhausted: newlyExhaustedUuids.length,
  };

  if (newlyExhaustedUuids.length > 0) {
    notifyStoreSalesNewlyExhausted(newlyExhaustedUuids);
  }

  return result;
}
